import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { Eye, EyeOff, Shield, Users, BarChart3, ArrowLeft } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';

const Login = ({ onLogin, onRegister }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    role: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    console.log('=== FORM SUBMIT CALLED ===');
    e.preventDefault();
    setLoading(true);

    console.log('Form data:', formData);
    console.log('Is login mode:', isLogin);
    console.log('onLogin function:', typeof onLogin);

    try {
      if (isLogin) {
        console.log('Attempting login with:', formData.username, formData.password);
        const result = await onLogin(formData.username, formData.password);
        console.log('Login result:', result);
        
        if (result.success) {
          toast.success(t('auth.toast_login_success'));
          // Force first-login password rotation for accounts still on a
          // seeded / default password (admin, demo users on a fresh install).
          if (result.must_change_password) {
            navigate('/change-password');
          } else {
            navigate('/dashboard');
          }
        } else {
          toast.error(result.error);
        }
      } else {
        // For public registration, always use social_actor role
        const result = await onRegister(
          formData.username, 
          formData.email, 
          formData.password, 
          'social_actor',
          formData.organization_name
        );
        if (result.success) {
          if (result.pending_approval) {
            toast.success(t('auth.toast_registration_pending'));
            // Stay on login page for pending approval
            setIsLogin(true);
            setFormData({
              username: '',
              password: '',
              email: '',
              role: '',
              organization_name: ''
            });
          } else {
            toast.success(t('auth.toast_registration_success'));
            navigate('/dashboard');
          }
        } else {
          toast.error(result.error);
        }
      }
    } catch (error) {
      console.error('Login form error:', error);
      toast.error(t('auth.toast_unexpected_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 p-4">
      <div className="w-full max-w-4xl">
        <div className="absolute top-4 left-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate('/')}
            data-testid="back-to-website-btn"
            className="bg-white/80 backdrop-blur hover:bg-white shadow-sm"
            aria-label={t('auth.back_to_website', 'Back to website')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('auth.back_to_website', 'Back to website')}
          </Button>
        </div>
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src="/assets/isisp_logo.png" 
              alt="ISISP Logo" 
              className="h-32 w-auto"
            />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            {t('auth.platform_name')}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('auth.platform_description')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Login/Register Form */}
          <Card className="glass-card border-0 shadow-2xl">
            <CardHeader className="text-center pb-4">
              {/* Greece 2.0 / NextGenerationEU Logo — clickable, opens the
                  official RRF Greece 2.0 portal in a new tab. */}
              <div className="flex justify-center mb-4">
                <a
                  href="https://greece20.gov.gr/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block transition-transform hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded-md"
                  data-testid="login-rrf-logo-link"
                  title="Greece 2.0 — National Recovery and Resilience Plan (opens in a new tab)"
                  aria-label="Greece 2.0 — NextGenerationEU — visit greece20.gov.gr"
                >
                  <img 
                    src="/greece_nextgen_logo.png" 
                    alt="Greece 2.0 - NextGenerationEU" 
                    className="max-w-full h-auto"
                    style={{ maxHeight: '120px' }}
                  />
                </a>
              </div>
              
              <CardTitle className="text-2xl font-bold text-gray-800">
                {isLogin ? t('auth.welcome_back_title') : t('auth.create_account')}
              </CardTitle>
              <p className="text-gray-600">
                {isLogin 
                  ? t('auth.signin_subtitle')
                  : t('auth.register_subtitle')}
              </p>
            </CardHeader>
            <CardContent>
              <Tabs value={isLogin ? "login" : "register"} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger 
                    value="login" 
                    onClick={() => setIsLogin(true)}
                    data-testid="login-tab"
                  >
                    {t('auth.sign_in')}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="register" 
                    onClick={() => navigate('/register')}
                    data-testid="register-tab"
                  >
                    {t('auth.register')}
                  </TabsTrigger>
                </TabsList>

                <form onSubmit={handleSubmit} className="space-y-4" aria-label={isLogin ? 'Sign in form' : 'Register form'}>
                  <div className="space-y-2">
                    <Label htmlFor="username" required>{t('auth.username')}</Label>
                    <Input
                      id="username"
                      type="text"
                      value={formData.username}
                      onChange={(e) => handleInputChange('username', e.target.value)}
                      placeholder={t('auth.username_placeholder')}
                      required
                      data-testid="username-input"
                      className="h-12"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      autoComplete="username"
                    />
                  </div>

                  {!isLogin && (
                    <div className="space-y-2">
                      <Label htmlFor="email" required>{t('auth.email')}</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder={t('auth.email_placeholder')}
                        required
                        data-testid="email-input"
                        className="h-12"
                        autoComplete="email"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="password" required>{t('auth.password')}</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        placeholder={t('auth.password_placeholder')}
                        required
                        data-testid="password-input"
                        className="h-12 pr-12"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        autoComplete={isLogin ? "current-password" : "new-password"}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        aria-pressed={showPassword}
                        title={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                      </button>
                    </div>
                  </div>

                  {!isLogin && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="organization" required>{t('auth.organization_name')}</Label>
                        <Input
                          id="organization"
                          type="text"
                          value={formData.organization_name || ''}
                          onChange={(e) => handleInputChange('organization_name', e.target.value)}
                          placeholder={t('auth.organization_placeholder')}
                          required
                          className="h-12"
                        />
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                          <strong>{t('auth.registration_note_title')}:</strong> {t('auth.registration_note')}
                        </p>
                      </div>
                    </>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-lg transition-all duration-200 hover:shadow-lg"
                    disabled={loading}
                    data-testid="auth-submit-button"
                    onClick={(e) => {
                      console.log('=== BUTTON CLICKED ===');
                      // Don't prevent default here since we want form submission
                    }}
                  >
                    {loading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>{isLogin ? t('auth.signing_in') : t('auth.creating_account')}</span>
                      </div>
                    ) : (
                      isLogin ? t('auth.sign_in') : t('auth.create_account')
                    )}
                  </Button>

                  {isLogin && (
                    <div className="text-center mt-4">
                      <a 
                        href="/forgot-password" 
                        className="text-sm text-blue-700 hover:text-blue-800 hover:underline"
                        data-testid="forgot-password-link"
                      >
                        {t('auth.forgot_password')}
                      </a>
                    </div>
                  )}
                </form>
              </Tabs>
            </CardContent>
          </Card>

          {/* Platform Features */}
          <div className="space-y-6">
            <Card className="glass-card border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BarChart3 className="h-6 w-6 text-blue-600" />
                  <span>{t('auth.features_title')}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Users className="h-5 w-5 text-green-700 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('auth.feature_multi_role_title')}</h4>
                    <p className="text-sm text-gray-600">{t('auth.feature_multi_role_desc')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <BarChart3 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('auth.feature_kpi_title')}</h4>
                    <p className="text-sm text-gray-600">{t('auth.feature_kpi_desc')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Shield className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('auth.feature_certification_title')}</h4>
                    <p className="text-sm text-gray-600">{t('auth.feature_certification_desc')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;